import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import styled from 'styled-components'
import { CircularProgress, Switch } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PublicIcon from '@mui/icons-material/Public'
import { Tooltip } from '@mui/material'
import { toast } from 'react-toastify'
import LogoShort from 'assets/images/bp-logo-orange.png'
import MarkdownRenderer from 'components/MarkdownRenderer'
import APIService from 'services/APIService'
import { PRIVATE_PATHS } from 'utils/paths'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import GuruPhotoSources from 'features/bpDetails/Components/guru/GuruPhotoSources'

const formatDateTime = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const GuruSharePage = () => {
  // The same component renders for `/bp-list/:id/guru-shares/:shareId` and
  // `/portfolios/:portfolioId/guru-shares/:shareId`. The :id / :portfolioId
  // params are informational — the API is keyed off the shareId alone.
  const { id: bpId, portfolioId, shareId } = useParams()
  const navigate = useNavigate()
  const [share, setShare] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [insightFile, setInsightFile] = useState(null)
  const [publishBusy, setPublishBusy] = useState(false)
  const [expiresAt, setExpiresAt] = useState('')

  // Whether this URL was reached via a portfolio path. Used only to choose
  // the right copy on a 403; the real access check is server-side.
  const isPortfolioUrl = !!portfolioId

  useEffect(() => {
    if (!shareId) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const api = APIService()
        const res = await api.get(`/guru-shares/${shareId}`)
        if (!cancelled) setShare(res?.data?.data || null)
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status
          if (status === 403) {
            setError(isPortfolioUrl
              ? "You don't have access to this portfolio, so you can't open this shared Q&A."
              : "You don't have access to this Building Passport, so you can't open this shared Q&A."
            )
          } else if (status === 404) {
            setError('This shared Q&A no longer exists.')
          } else {
            setError('Could not load this shared Q&A. Please try again.')
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [shareId, isPortfolioUrl])

  // Hydrate the expires-at picker from the loaded share so the input shows
  // the existing expiry. We strip the timezone suffix because <input
  // type="datetime-local"> expects "YYYY-MM-DDTHH:MM" without a zone.
  useEffect(() => {
    if (share?.publicExpiresAt) {
      const d = new Date(share.publicExpiresAt)
      if (!Number.isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0')
        setExpiresAt(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
        )
      }
    } else if (!share?.isPublic) {
      setExpiresAt('')
    }
  }, [share?.publicExpiresAt, share?.isPublic])

  // Optional override skips the `expiresAt` state read so callers can
  // change expiry + publish in the same tick without racing the setState
  // (e.g. the Clear button).
  const togglePublic = async (nextPublic, expiryOverride) => {
    if (!shareId) return
    const expiry = expiryOverride !== undefined ? expiryOverride : expiresAt
    setPublishBusy(true)
    try {
      const api = APIService()
      if (nextPublic) {
        const payload = {}
        if (expiry) {
          const d = new Date(expiry)
          if (Number.isNaN(d.getTime())) {
            toast.error('Please enter a valid expiry date.')
            return
          }
          payload.expires_at = d.toISOString()
        }
        const res = await api.post(`/guru-shares/${shareId}/publish`, payload)
        setShare(res?.data?.data || null)
        toast.success('Share is now public')
      } else {
        const res = await api.post(`/guru-shares/${shareId}/unpublish`)
        setShare(res?.data?.data || null)
        toast.info('Public access removed')
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not update sharing settings.'
      toast.error(msg)
    } finally {
      setPublishBusy(false)
    }
  }

  const copyPublicUrl = async () => {
    if (!share?.publicUrl) return
    try {
      await navigator.clipboard.writeText(share.publicUrl)
      toast.success('Public link copied')
    } catch {
      toast.info(`Copy this link: ${share.publicUrl}`)
    }
  }

  const showsAsPhoto = (c) => c.isPhoto && c.accessible !== false
  const photoCitations = (share?.citations || []).filter(showsAsPhoto)
  const nonPhotoCitations = (share?.citations || []).filter((c) => !showsAsPhoto(c))

  const goBack = () => {
    // Portfolio-scoped share: land on the portfolio detail page. The
    // portfolio's Guru tab is the natural follow-up surface.
    if (share?.portfolio?.id) {
      navigate(`/portfolios/${share.portfolio.id}`)
      return
    }
    if (portfolioId) {
      navigate(`/portfolios/${portfolioId}`)
      return
    }
    // BP-scoped: land on the BP root. Guru chat is gated on full access, so
    // restricted users would see an empty page. BpDetails picks a sensible
    // default folder per role.
    if (bpId || share?.buildingPassport?.id) {
      navigate(`${PRIVATE_PATHS.bpList}/${bpId || share.buildingPassport.id}`)
    } else {
      navigate(PRIVATE_PATHS.bpList)
    }
  }

  return (
    <Page>
      <BackLink onClick={goBack}>
        <ArrowBackIcon style={{ fontSize: 16 }} />
        Back to Building Passport
      </BackLink>

      {loading && (
        <Card>
          <LoadingRow>
            <CircularProgress size={20} sx={{ color: '#D24724' }} />
            <span>Loading shared Q&amp;A…</span>
          </LoadingRow>
        </Card>
      )}

      {!loading && error && (
        <Card>
          <ErrorMsg>{error}</ErrorMsg>
        </Card>
      )}

      {!loading && !error && share && (
        <>
          <Header>
            <Heading>
              <ChatBubbleOutlineIcon style={{ fontSize: 18, color: '#D24724' }} />
              Shared from Guru chat
            </Heading>
            <Sub>
              {share.scope === 'portfolio' && share.portfolio?.name && (
                <>
                  Portfolio: <BpLink to={`/portfolios/${share.portfolio.id}`}>
                    {share.portfolio.name}
                  </BpLink>
                  {' · '}
                </>
              )}
              {share.scope !== 'portfolio' && share.buildingPassport?.name && (
                <>
                  Building Passport: <BpLink to={`${PRIVATE_PATHS.bpList}/${share.buildingPassport.id}/guru`}>
                    {share.buildingPassport.name}
                  </BpLink>
                  {' · '}
                </>
              )}
              {share.creator?.name && <>Shared by {share.creator.name} · </>}
              {formatDateTime(share.createdAt)}
            </Sub>
          </Header>

          <ShareControls>
            <ShareControlsRow>
              <ShareToggleLabel>
                <PublicIcon style={{ fontSize: 18, color: share.isPublic ? '#D24724' : '#94a3b8' }} />
                <div>
                  <ShareToggleTitle>Public link</ShareToggleTitle>
                  <ShareToggleHint>
                    {share.isPublic
                      ? 'Anyone with the link can view this Q&A — no login required.'
                      : share.scope === 'portfolio'
                        ? 'Off — only Building Passport users this portfolio is shared with can open this share.'
                        : 'Off — only users with access to this Building Passport can open this share.'}
                  </ShareToggleHint>
                </div>
              </ShareToggleLabel>
              <Switch
                checked={!!share.isPublic}
                onChange={(e) => togglePublic(e.target.checked)}
                disabled={publishBusy}
                color="warning"
              />
            </ShareControlsRow>

            {share.isPublic && (
              <>
                <PublicUrlRow>
                  <PublicUrlInput
                    type="text"
                    value={share.publicUrl || ''}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                  <CopyButton type="button" onClick={copyPublicUrl}>
                    <ContentCopyIcon style={{ fontSize: 14 }} />
                    Copy
                  </CopyButton>
                </PublicUrlRow>

                <ExpiryRow>
                  <ExpiryLabel>Expires</ExpiryLabel>
                  <ExpiryInput
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    disabled={publishBusy}
                  />
                  <ExpiryAction
                    type="button"
                    onClick={() => togglePublic(true)}
                    disabled={publishBusy}
                  >
                    {share.publicExpiresAt ? 'Update' : 'Set expiry'}
                  </ExpiryAction>
                  {share.publicExpiresAt && (
                    <ExpiryClear
                      type="button"
                      onClick={() => { setExpiresAt(''); togglePublic(true, '') }}
                      disabled={publishBusy}
                    >
                      Clear
                    </ExpiryClear>
                  )}
                </ExpiryRow>
              </>
            )}

            {(share.viewCount || 0) > 0 && (
              <ViewSummary>
                <VisibilityOutlinedIcon style={{ fontSize: 14, color: '#64748b' }} />
                Viewed {share.viewCount} {share.viewCount === 1 ? 'time' : 'times'}
                {share.lastViewedAt && <> · last on {formatDateTime(share.lastViewedAt)}</>}
                {share.lastViewer?.name && <> by {share.lastViewer.name}</>}
              </ViewSummary>
            )}
          </ShareControls>

          <QuestionCard>
            <BubbleLabel>Question</BubbleLabel>
            <BubbleBody>{share.question}</BubbleBody>
          </QuestionCard>

          <AnswerCard>
            <AnswerHeader>
              <GuruAvatar src={LogoShort} alt="Guru" />
              <BubbleLabel>Guru&rsquo;s answer</BubbleLabel>
            </AnswerHeader>
            <AnswerBody>
              <MarkdownRenderer content={share.answer} />
            </AnswerBody>

            {photoCitations.length > 0 && (
              <GuruPhotoSources
                citations={photoCitations}
                fallbackBpId={share.buildingPassport?.id}
              />
            )}

            {nonPhotoCitations.length > 0 && (
              <Section>
                <SectionLabel>Sources ({nonPhotoCitations.length})</SectionLabel>
                <SourceList>
                  {nonPhotoCitations.map((citation, i) => {
                    // Per-citation access flag from the backend. Defaults to
                    // true for backwards-compat with shares created before
                    // the flag existed — those still try to open and rely on
                    // the modal's own error path.
                    const accessible = citation.accessible !== false
                    if (!accessible) {
                      return (
                        <Tooltip
                          key={citation.id || i}
                          title="You don't have permission to view this source. Ask the share creator or a Building Passport admin if you need access."
                          placement="top"
                        >
                          <SourceRow as="div" $locked>
                            <LockOutlinedIcon style={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />
                            <SourceName $muted>{citation.name}</SourceName>
                            <LockedTag>Restricted</LockedTag>
                          </SourceRow>
                        </Tooltip>
                      )
                    }
                    // Citation may carry its own buildingPassportId (portfolio
                    // shares) — fall back to the share's BP when absent
                    // (BP-scoped shares created before per-citation BP).
                    const citationBpId = citation.buildingPassportId || share.buildingPassport?.id
                    return (
                      <SourceRow
                        key={citation.id || i}
                        onClick={() => {
                          if (!citation.id || !citationBpId) return
                          setInsightFile({
                            id: citation.id,
                            displayName: citation.name,
                            buildingPassportId: citationBpId,
                            buildingPassportName: share.buildingPassport?.name,
                          })
                        }}
                      >
                        <SourceName>{citation.name}</SourceName>
                        {citation.score != null && (
                          <SourceScore>{Math.round(citation.score * 100)}% match</SourceScore>
                        )}
                      </SourceRow>
                    )
                  })}
                </SourceList>
              </Section>
            )}

            {share.webSources?.length > 0 && (
              <Section>
                <SectionLabel>Web sources consulted</SectionLabel>
                <WebList>
                  {share.webSources.map((domain, i) => (
                    <WebChip
                      key={i}
                      onClick={() => window.open(`https://${domain}`, '_blank', 'noopener,noreferrer')}
                    >
                      {domain}
                    </WebChip>
                  ))}
                </WebList>
              </Section>
            )}

            <Disclaimer>
              AI-generated response — may not fully reflect your documents. Always verify critical information.
            </Disclaimer>
          </AnswerCard>
        </>
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </Page>
  )
}

export default GuruSharePage

const Page = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) { padding: 16px; }
`

const BackLink = styled.button`
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  margin-bottom: 16px;
  &:hover { text-decoration: underline; }
`

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 0;
  font-size: 14px;
  color: #64748b;
`

const ErrorMsg = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 14px;
  color: #b45309;
`

const Header = styled.div`
  margin-bottom: 18px;
`

const Heading = styled.h1`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`

const Sub = styled.div`
  font-size: 12px;
  color: #64748b;
`

const BpLink = styled(Link)`
  color: #D24724;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`

const QuestionCard = styled(Card)`
  margin-bottom: 14px;
  background: #fafbfc;
`

const AnswerCard = styled(Card)`
  margin-bottom: 14px;
`

const BubbleLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
`

const BubbleBody = styled.div`
  font-size: 15px;
  line-height: 1.55;
  color: #0f172a;
  white-space: pre-wrap;
`

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`

const GuruAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
`

const AnswerBody = styled.div`
  font-size: 15px;
  line-height: 1.55;
  color: #0f172a;
`

const Section = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
`

const SectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
`

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  background: ${({ $locked }) => ($locked ? '#f8fafc' : '#f8fafc')};
  border: 1px solid ${({ $locked }) => ($locked ? '#e2e8f0' : '#e5e7eb')};
  border-radius: 8px;
  cursor: ${({ $locked }) => ($locked ? 'not-allowed' : 'pointer')};
  opacity: ${({ $locked }) => ($locked ? 0.7 : 1)};
  transition: background 0.15s;
  &:hover { background: ${({ $locked }) => ($locked ? '#f8fafc' : '#f1f5f9')}; }
`

const SourceName = styled.div`
  font-size: 13px;
  color: ${({ $muted }) => ($muted ? '#94a3b8' : '#0f172a')};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SourceScore = styled.span`
  font-size: 11px;
  color: #64748b;
  flex-shrink: 0;
`

const LockedTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
`

const WebList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const WebChip = styled.span`
  font-size: 12px;
  color: #33becc;
  background: #e6f7f9;
  padding: 2px 10px;
  border-radius: 12px;
  cursor: pointer;
  &:hover { background: #bbdefb; }
`

const Disclaimer = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`

const ShareControls = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const ShareControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const ShareToggleLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

const ShareToggleTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
`

const ShareToggleHint = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  max-width: 540px;
`

const PublicUrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`

const PublicUrlInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #0f172a;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
`

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: #D24724;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #b53a1d; }
`

const ExpiryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`

const ExpiryLabel = styled.div`
  font-size: 12px;
  color: #64748b;
`

const ExpiryInput = styled.input`
  font-size: 12px;
  color: #0f172a;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
`

const ExpiryAction = styled.button`
  font-size: 12px;
  font-weight: 600;
  color: #D24724;
  background: transparent;
  border: 1px solid #D24724;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #fff5f1; }
`

const ExpiryClear = styled.button`
  font-size: 12px;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 4px;
  &:hover:not(:disabled) { text-decoration: underline; }
`

const ViewSummary = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
`
